
import { useState, useEffect } from 'react';

interface Feature {
  id: number;
  icon: string;
  title: string;
  description: string;
  color: string;
}

export default function FeaturesSection() {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const features: Feature[] = [
    {
      id: 1,
      icon: 'ri-flash-line',
      title: 'Hızlı Teslimat',
      description: 'Projelerinizi zamanında ve kaliteli bir şekilde teslim ediyoruz.',
      color: 'blue'
    },
    {
      id: 2,
      icon: 'ri-shield-check-line',
      title: 'Güvenli Çözümler',
      description: 'En güncel güvenlik standartlarıyla projelerinizi koruyoruz.',
      color: 'cyan'
    },
    {
      id: 3,
      icon: 'ri-customer-service-2-line',
      title: '7/24 Destek',
      description: 'Proje sonrası sürekli destek ve bakım hizmeti sunuyoruz.',
      color: 'purple'
    },
    {
      id: 4,
      icon: 'ri-bar-chart-line',
      title: 'Performans Odaklı',
      description: 'Yüksek performanslı ve optimize edilmiş çözümler geliştiriyoruz.',
      color: 'green'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('ozellikler');
    if (section) observer.observe(section);

    // Auto-rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % features.length);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'from-blue-500/20 to-blue-600/20 border-blue-400/30 text-blue-400',
      cyan: 'from-cyan-500/20 to-cyan-600/20 border-cyan-400/30 text-cyan-400',
      purple: 'from-purple-500/20 to-purple-600/20 border-purple-400/30 text-purple-400',
      green: 'from-green-500/20 to-green-600/20 border-green-400/30 text-green-400'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <section id="ozellikler" className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-star-line text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Neden Biz?</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Fark Yaratan Özelliklerimiz
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Dijital dünyada öne çıkmanızı sağlayan özel yaklaşımımız ve 
            profesyonel hizmet kalitemizle fark yaratıyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Features List */}
          <div className={`space-y-6 transition-all duration-1000 transform ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-20 opacity-0'
          }`}>
            {features.map((feature, index) => (
              <div
                key={feature.id}
                onMouseEnter={() => setActiveFeature(index)}
                className={`group relative bg-gradient-to-br backdrop-blur-sm rounded-2xl border p-6 transition-all duration-500 cursor-pointer transform hover:scale-105 ${
                  index === activeFeature
                    ? `${getColorClasses(feature.color)} shadow-2xl`
                    : 'from-gray-800/30 to-gray-900/30 border-gray-700/30 hover:border-gray-600/50'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                    index === activeFeature
                      ? `bg-gradient-to-br ${getColorClasses(feature.color).split(' ')[0]} ${getColorClasses(feature.color).split(' ')[1]}`
                      : 'bg-gray-700/50'
                  }`}>
                    <i className={`${feature.icon} text-2xl ${
                      index === activeFeature 
                        ? getColorClasses(feature.color).split(' ')[3] 
                        : 'text-gray-400'
                    }`}></i>
                  </div>
                  
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      index === activeFeature ? 'text-white' : 'text-gray-300'
                    }`}>
                      {feature.title}
                    </h3>
                    <p className={`leading-relaxed transition-colors duration-300 ${
                      index === activeFeature ? 'text-gray-200' : 'text-gray-400'
                    }`}>
                      {feature.description}
                    </p>
                  </div>

                  {index === activeFeature && (
                    <div className="absolute -right-2 -top-2 w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center animate-pulse">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                  )}
                </div>

                {/* Progress bar */}
                {index === activeFeature && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800 rounded-b-2xl overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 animate-pulse"></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Visual Representation */}
          <div className={`relative transition-all duration-1000 delay-300 transform ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-20 opacity-0'
          }`}>
            <div className="relative w-full h-96 bg-gradient-to-br from-gray-800/30 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 overflow-hidden">
              {/* Interactive Dashboard Mockup */}
              <div className="absolute inset-4 bg-gray-900/80 rounded-2xl border border-gray-700/50 p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>

                {/* Active Feature Display */}
                <div className="text-center">
                  <div className={`w-24 h-24 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${getColorClasses(features[activeFeature].color).split(' ')[0]} ${getColorClasses(features[activeFeature].color).split(' ')[1]} flex items-center justify-center`}>
                    <i className={`${features[activeFeature].icon} text-3xl ${getColorClasses(features[activeFeature].color).split(' ')[3]}`}></i>
                  </div>
                  
                  <h4 className="text-xl font-bold text-white mb-2">
                    {features[activeFeature].title}
                  </h4>
                  
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {features[activeFeature].description}
                  </p>

                  {/* Animated Progress */}
                  <div className="mt-6 space-y-2">
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>Performans</span>
                      <span>98%</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2">
                      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full w-[98%] animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl rotate-12 animate-float opacity-80"></div>
              <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl -rotate-12 animate-float delay-1000 opacity-60"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
